'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Center, Loader, Stack, Text } from '@mantine/core';
import { useAuth } from '@/hooks/useAuth';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Route guard component that protects routes from unauthenticated access.
 * Shows a loading spinner while checking authentication state.
 * Redirects to /login if user is not authenticated.
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, error, login } = useAuth();
  const router = useRouter();

  useEffect(() => {
    // If not loading and not authenticated, redirect to login
    if (!isLoading && !isAuthenticated) {
      router.push('/login');
    }
  }, [isLoading, isAuthenticated, router]);

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <Loader size="lg" color="primary" />
          <Text c="dimmed">Checking authentication...</Text>
        </Stack>
      </Center>
    );
  }

  // Show error state if authentication failed
  if (error) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <Text c="red" fw={500}>Authentication Error</Text>
          <Text c="dimmed" size="sm">{error.message}</Text>
          <Text 
            c="primary" 
            size="sm" 
            style={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => login()}
          >
            Try logging in again
          </Text>
        </Stack>
      </Center>
    );
  }

  // If not authenticated, don't render children (redirect will happen via useEffect)
  if (!isAuthenticated) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <Loader size="lg" color="primary" />
          <Text c="dimmed">Redirecting to login...</Text>
        </Stack>
      </Center>
    );
  }

  // User is authenticated, render children
  return <>{children}</>;
}
